/** Separate single-flight background stream for read-only split projections. */
import { db } from "./db";
import { getLinkedSplits, pullLinkedSplit, readSession, SplitHttpError, type LinkedPullRow } from "./backend";
import { LINKED_LIST_KEY, evictLinkedSplit, linkedCursorKey, readLinkedList } from "./linked_splits";
import { fromWireLinkedGroup, fromWireLinkedExpense, fromWireLinkedTransfer, WireRowError } from "./types";
import { SplitRowError } from "@/domain/splits";
const DIAGNOSTIC_LIMIT = 100;
export interface LinkedSyncStatus { state: "idle" | "syncing" | "error"; unreadable: number; failures: number; error: string | null; lastSyncedAt: string | null }
const clean: LinkedSyncStatus = { state: "idle", unreadable: 0, failures: 0, error: null, lastSyncedAt: null };
let status = { ...clean };
let inFlight: Promise<void> | null = null;
let generation = 0;
const listeners = new Set<() => void>();
export const getLinkedSyncStatus = (): LinkedSyncStatus => status;
export function subscribeLinkedSync(listener: () => void): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; }
function publish(next: LinkedSyncStatus) { status = next; for (const listener of listeners) listener(); }
export function resetLinkedSync(): void { generation++; inFlight = null; publish({ ...clean }); }
function validateRow(row: LinkedPullRow): void {
  if (row.deleted && row.payload === "") return;
  const raw: unknown = JSON.parse(row.payload);
  const parsed = row.table === "split_groups" ? fromWireLinkedGroup(raw) : row.table === "split_expenses" ? fromWireLinkedExpense(raw) : row.table === "split_transfers" ? fromWireLinkedTransfer(raw) : null;
  if (!parsed || parsed.id !== row.row_id) throw new SplitRowError("Unknown split table or mismatched row identity.");
}
export async function syncLinkedSplits(fetchImpl: typeof fetch = fetch): Promise<void> {
  if (inFlight) return inFlight;
  const session = readSession();
  if (!session || (typeof navigator !== "undefined" && !navigator.onLine)) return;
  const runGeneration = generation;
  const current = () => generation === runGeneration && readSession()?.accountId === session.accountId && readSession()?.token === session.token;
  inFlight = (async () => {
    let unreadable = 0, failures = 0;
    const errors: string[] = [];
    publish({ ...status, state: "syncing", error: null });
    try {
      const links = await getLinkedSplits(fetchImpl);
      if (!current()) return;
      const previous = await readLinkedList();
      for (const link of previous) if (!links.some(next => next.link_id === link.link_id)) await evictLinkedSplit(link.link_id);
      await db.meta.put({ key: LINKED_LIST_KEY, value: JSON.stringify(links) });
      for (const link of links) {
        if (!current()) return;
        if (link.ended) { await evictLinkedSplit(link.link_id); continue; }
        try {
          let since = Number((await db.meta.get(linkedCursorKey(link.link_id)))?.value ?? "0");
          let more = true;
          while (more) {
            const page = await pullLinkedSplit(link.link_id, since, fetchImpl);
            if (!current()) return;
            if (page.has_more && page.next_since <= since) throw new Error("Shared sync did not advance. Try syncing again.");
            await db.transaction("rw", db.linkedSplitRows, db.meta, async () => {
              for (const row of page.rows) {
                try { validateRow(row); }
                catch (cause) {
                  if (!(cause instanceof SyntaxError || cause instanceof WireRowError || cause instanceof SplitRowError)) throw cause;
                  unreadable++;
                  console.warn(`Skipped unreadable linked ${row.table} row (${row.row_id}):`, cause);
                  try { await db.meta.put({ key: `split:quarantine:${link.link_id}:${String(row.seq)}`, value: JSON.stringify({ table: row.table, row_id: row.row_id, reason: "invalid_row" }) }); }
                  catch (diagnosticError) { console.warn("Could not record shared quarantine diagnostic:", diagnosticError); }
                  continue;
                }
                const key: [string, string, string] = [link.link_id, row.table, row.row_id];
                const old = await db.linkedSplitRows.get(key);
                if (old?.updatedAt && old.updatedAt > row.updated_at) continue;
                if (row.deleted && row.payload === "") {
                  if (old) await db.linkedSplitRows.put({ ...old, deleted: true, updatedAt: row.updated_at });
                } else await db.linkedSplitRows.put({ linkId: link.link_id, table: row.table, rowId: row.row_id, payload: row.payload, deleted: row.deleted, updatedAt: row.updated_at });
              }
              const diagnostics = await db.meta.where("key").startsWith("split:quarantine:").primaryKeys();
              if (diagnostics.length > DIAGNOSTIC_LIMIT) await db.meta.bulkDelete(diagnostics.slice(0, diagnostics.length - DIAGNOSTIC_LIMIT)); // invariant-exempt: bounded local diagnostics, not ledger rows
              await db.meta.put({ key: linkedCursorKey(link.link_id), value: String(page.next_since) });
            });
            since = page.next_since; more = page.has_more;
          }
        } catch (cause) {
          if (!current()) return;
          if (cause instanceof SplitHttpError && (cause.status === 403 || cause.status === 404)) {
            await evictLinkedSplit(link.link_id);
            const cached = await readLinkedList();
            await db.meta.put({ key: LINKED_LIST_KEY, value: JSON.stringify(cached.map(item => item.link_id === link.link_id ? { ...item, ended: true } : item)) });
          }
          failures++; errors.push(cause instanceof Error ? cause.message : String(cause));
        }
      }
    } catch (cause) { failures++; errors.push(cause instanceof Error ? cause.message : String(cause)); }
    if (current()) publish({ state: failures ? "error" : "idle", unreadable, failures,
      error: [...(unreadable ? [`${String(unreadable)} shared rows could not be read. Ask the owner to check those splits.`] : []), ...errors].join(" ") || null,
      lastSyncedAt: new Date().toISOString() });
  })();
  try { await inFlight; } finally { if (generation === runGeneration) inFlight = null; }
}
